import { useCallback, useRef } from 'react';
import { ApiError, apiRequest } from '../../api/client';
import type { RecordedSet, SessionData, FeedbackData, FeedbackResponse } from '../types';

interface SessionApi {
  getSession: (sessionId: string) => Promise<SessionData>;
  startSession: (sessionId: string) => Promise<void>;
  recordSet: (sessionId: string, set: RecordedSet) => Promise<void>;
  completeSession: (sessionId: string) => Promise<void>;
  submitFeedback: (sessionId: string, feedback: FeedbackData) => Promise<FeedbackResponse>;
}

/**
 * The original app talked to the C# server with `credentials: 'same-origin'`
 * against `/api/sessions/*`. Here the SPA is served from a CDN on a different
 * origin to the API and authenticates with bearer tokens, so cookies would
 * never be sent. Routing through `apiRequest` also means a workout picks up
 * access-token refresh — issuing raw fetches would sign the user out mid-set
 * once the token expired.
 */
async function withRetry<T>(send: () => Promise<T>, retries = 2): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await send();
    } catch (error) {
      // 503 means the API is briefly unavailable, not that the request was
      // wrong; the original app backed off and retried rather than dropping
      // the user out of a session in progress.
      const retryable = error instanceof ApiError && error.status === 503;
      if (!retryable || attempt >= retries) throw error;
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
}

export function useSessionApi(): SessionApi {
  const controllerRef = useRef<AbortController | null>(null);

  const getSession = useCallback(async (sessionId: string): Promise<SessionData> => {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;
    return withRetry(() => apiRequest<SessionData>(`/api/v1/sessions/${sessionId}`, { signal }));
  }, []);

  const startSession = useCallback(async (sessionId: string): Promise<void> => {
    await withRetry(() =>
      apiRequest<void>(`/api/v1/sessions/${sessionId}/start`, { method: 'POST' }),
    );
  }, []);

  const recordSet = useCallback(async (sessionId: string, set: RecordedSet): Promise<void> => {
    await withRetry(() =>
      apiRequest<void>(`/api/v1/sessions/${sessionId}/sets`, { method: 'POST', body: set }),
    );
  }, []);

  const completeSession = useCallback(async (sessionId: string): Promise<void> => {
    await withRetry(() =>
      apiRequest<void>(`/api/v1/sessions/${sessionId}/complete`, { method: 'POST' }),
    );
  }, []);

  const submitFeedback = useCallback(
    async (sessionId: string, feedback: FeedbackData): Promise<FeedbackResponse> => {
      return withRetry(() =>
        apiRequest<FeedbackResponse>(`/api/v1/sessions/${sessionId}/feedback`, {
          method: 'POST',
          body: feedback,
        }),
      );
    },
    [],
  );

  return { getSession, startSession, recordSet, completeSession, submitFeedback };
}
